import { useState } from 'react'
import { isSafeCatalogImageRef } from '../../rfq/AddPartsModal'
import type { CatalogBrandTile } from '../types'

export interface ShopByBrandProps {
  readonly brands: readonly CatalogBrandTile[]
  /**
   * UID-416 / ADR-0131 D10: this component stays PRESENTATION-ONLY and has
   * no opinion on what a click does — mirroring `ShopByCategory`'s contract.
   * The container (`CatalogBrowsePage`) owns navigation and MUST validate
   * `linkPath` (client-side defence in depth, D4) before acting on it; this
   * component hands the value straight through.
   */
  readonly onBrandClick: (linkPath: string) => void
}

/**
 * A brand tile's logo (UID-416 / ADR-0131 D7): `<img src>`, never a CSS
 * `background-image` (the design source's raw markup uses one — React
 * escapes an `src` attribute, so there is no `url()` context for a
 * server-supplied string to break out of; copying the mock's markup would be
 * the one genuinely unsafe choice here, same ruling as ADR-0126 D17).
 * `alt=""` + `aria-hidden="true"`: the brand NAME is the accessible label
 * (rendered as real text below), so the logo is decorative.
 *
 * Validated with the EXISTING `isSafeCatalogImageRef` (apis D5's explicit
 * instruction to reuse it, not build a second image-ref checker) — a
 * `null`/unsafe/broken logoRef all resolve to the SAME placeholder (an empty,
 * fixed-height box), and the reserved height means a missing logo can never
 * reflow the tile (apis D9 / AC4).
 */
function BrandTileLogo({ logoRef }: { readonly logoRef: string | null }) {
  const [hasErrored, setHasErrored] = useState(false)
  const [lastLogoRef, setLastLogoRef] = useState(logoRef)
  if (logoRef !== lastLogoRef) {
    setLastLogoRef(logoRef)
    setHasErrored(false)
  }

  const canRender = isSafeCatalogImageRef(logoRef) && !hasErrored

  return (
    <div className="catalog-brand-tile-logo">
      {canRender && (
        <img
          src={logoRef}
          alt=""
          aria-hidden="true"
          className="catalog-brand-tile-logo-img"
          onError={() => setHasErrored(true)}
        />
      )}
    </div>
  )
}

/**
 * The "Shop by brand" panel (UID-416 / ADR-0131 D10, mirroring
 * `ShopByCategory`'s presentation-only contract). Renders ONE grid,
 * unconditionally, whether it holds one tile or many — D10.2's ruling: a
 * single tile alone in the first grid cell is the correct render, not a
 * special case, and this component MUST NOT sort, stretch, or fill the
 * empty cell with a placeholder. Response order is render order (the BFF
 * relays the Catalog service's curated `display_order` verbatim).
 *
 * Deliberately carries NO brand metadata — no label map, no logo map, no
 * ordering logic. That absence (not merely an intention) is what makes
 * "add/remove/reorder brands with no frontend code change" true (apis AC3):
 * every visible fact about a brand comes from the fetched tile.
 */
export function ShopByBrand({ brands, onBrandClick }: ShopByBrandProps) {
  return (
    <section aria-labelledby="shop-by-brand-heading" className="catalog-shop-by-brand">
      {/* h2, not ShopByCategory's h1: CatalogBrowsePage already has several
       * top-level sections, and a second h1 would break the page's heading
       * hierarchy. The "landmark structure" this mirrors is the
       * section+aria-labelledby+heading shape, not the exact heading level. */}
      <h2 id="shop-by-brand-heading" className="catalog-shop-by-brand-heading">
        Shop by brand
      </h2>
      <div className="catalog-brand-grid">
        {brands.map((brand) => (
          <button
            key={brand.brandKey}
            type="button"
            className="catalog-brand-tile"
            onClick={() => onBrandClick(brand.linkPath)}
          >
            <BrandTileLogo logoRef={brand.logoRef} />
            <span className="catalog-brand-tile-name">{brand.name}</span>
            {brand.countLabel && (
              <span className="catalog-brand-tile-count">{brand.countLabel}</span>
            )}
          </button>
        ))}
      </div>
    </section>
  )
}
